/**
 * Official profile · Votes & legislation (screenshots/29). Stats, view switch (Divided votes / Their nay votes /
 * Legislation filed) with LegislationRow-style rows ("THEIR VOTE" + YEA/NAY chip), and the side column: Pending
 * votes to watch (ForecastChip computed by forecastVote() from the API's real inputs), Committees (Chair badge),
 * What they file on (single-hue bars).
 */
import { useMemo, useState } from "react";
import { ArrowRight, ScrollText, Sparkles, TrendingDown, TrendingUp, MoveRight } from "lucide-react";
import { forecastVote } from "../forecast/forecast";
import { hydrateModel } from "../forecast/model";
import { mdy } from "./atoms";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import type { LegRow, OfficialRecord } from "./model";

type View = "divided" | "nays" | "filed";
const SHOWN = 6;

export function VotesTab({ rec }: { rec: OfficialRecord }) {
  const v = rec.votes;
  const [view, setView] = useState<View>("divided");
  const [all, setAll] = useState(false);
  const nays = useMemo(() => v.divided.filter((r) => r.vote === "nay"), [v.divided]);
  const rows = view === "divided" ? v.divided : view === "nays" ? nays : v.filedRows;
  const shown = all ? rows : rows.slice(0, SHOWN);
  const maxTopic = Math.max(1, ...v.fileTopics.map((t) => t.count));
  return (
    <div className="p-cols">
      <div className="p-main p-main--20">
        <div className="p-tiles">
          <Tile v={`${fmt(v.attendancePct)}%`} l="Attendance" s={`${v.absent} absent of ${v.rollCalls.toLocaleString("en-US")} key roll calls`} />
          <Tile v={v.nays} l="Nay votes" s="this term" />
          <Tile v={`${fmt(v.withMajorityPct)}%`} l="With the majority" s={`on ${v.dividedVotes} divided votes`} />
          <Tile v={v.filed} l="Filed as lead sponsor" s={`${v.passed} passed`} />
        </div>
        <div className="p-subview">
          <div className="p-seg" role="radiogroup" aria-label="Votes">
            {([["divided", `Divided votes · ${v.divided.length}`], ["nays", `Their nay votes · ${nays.length}`], ["filed", `Legislation filed · ${v.filed}`]] as const).map(([id, label]) => (
              <button key={id} role="radio" aria-checked={view === id} onClick={() => { setView(id); setAll(false); }}>{label}</button>
            ))}
          </div>
          {view !== "filed" && <span className="p-hint">Outcome shown as yea–nay</span>}
        </div>
        <List variant="plain" className="p-legrows">{shown.map((r) => <LegRowView key={r.matterId} r={r} signal={view !== "filed"} />)}</List>
        {!all && rows.length > SHOWN && (
          <button className="link-arrow p-more-link" onClick={() => setAll(true)}>
            {view === "divided" ? `All ${rows.length} divided votes` : view === "nays" ? `All ${rows.length} nay votes` : `All ${rows.length} items filed`}<ArrowRight size={14} aria-hidden />
          </button>
        )}
      </div>
      <aside className="p-side">
        {v.pending.length > 0 && (
          <section className="p-card">
            <div className="p-card__head"><h3>Pending votes to watch</h3><span className="ai-chip"><Sparkles size={11} aria-hidden />Forecast</span></div>
            {v.pending.map((x) => {
              const f = forecastVote(x.input, hydrateModel(v.model));
              const Icon = f.label === "likely" ? TrendingUp : f.label === "unlikely" ? TrendingDown : MoveRight;
              const text = f.label === "likely" ? `Likely yes · ${Math.round(f.p * 100)}%` : f.label === "unlikely" ? `Unlikely yes · ${Math.round(f.p * 100)}%` : `Toss-up · ${Math.round(f.p * 100)}%`;
              return (
                <a key={x.matterId} className="p-pending" href={`/dashboard/legislation/${encodeURIComponent(x.recordId)}`}>
                  <b>{x.title}</b>
                  <span className="p-pending__meta">
                    <small>{x.committee} · {x.daysWaiting} days waiting</small>
                    <span className={`forecast-chip forecast-chip--${f.label}`} title={`Range ${Math.round(f.low * 100)}–${Math.round(f.high * 100)}% · ${f.confidence[0].toUpperCase()}${f.confidence.slice(1)} confidence`}><Icon size={12} aria-hidden />{text}</span>
                  </span>
                </a>
              );
            })}
            <a className="link-arrow" href="/dashboard/legislation">All pending votes<ArrowRight size={14} aria-hidden /></a>
          </section>
        )}
        <section className="p-card">
          <h3>Committees · {v.committees.length}</h3>
          <ul className="p-committees">
            {v.committees.map((c) => <li key={c.name}><span>{c.name}</span>{c.chair && <span className="count-badge">Chair</span>}</li>)}
          </ul>
        </section>
        {v.fileTopics.length > 0 && (
          <section className="p-card">
            <h3>What they file on</h3>
            <p className="p-card__sub">{v.filed} items as lead sponsor this term, by topic</p>
            <div className="p-bars">
              {v.fileTopics.map((t) => (
                <div key={t.label} className="p-bars__row">
                  <span><span>{t.label}</span><b>{t.count}</b></span>
                  <span className="p-bars__track"><span style={{ width: `${(t.count / maxTopic) * 100}%` }} /></span>
                </div>
              ))}
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export const Tile = ({ v, l, s }: { v: React.ReactNode; l: string; s: string }) => (
  <div className="p-tile"><b>{v}</b><span>{l}</span><small>{s}</small></div>
);

function LegRowView({ r, signal }: { r: LegRow; signal: boolean }) {
  return (
    <ListRow className="p-leg" href={`/dashboard/legislation/${encodeURIComponent(r.recordId)}`}>
      <RowMain min={160} className="p-leg__main">
        <span className="p-leg__meta"><i className="type-badge" data-type="legislation" aria-hidden><ScrollText size={11} /></i><b>{r.recordId}</b><span>{r.category}</span></span>
        <RowTitle lines={2} className="p-leg__title">{r.title}</RowTitle>
        <RowSub><small>{r.committee}</small></RowSub>
      </RowMain>
      <RowCell width={170} className="p-leg__status">
        <span className={`p-outcome p-outcome--${r.outcome.tone}`}>{r.outcome.text}</span>
        <small>{mdy(r.date, { month: "short", day: "numeric", year: "numeric" })}</small>
      </RowCell>
      <RowCell width={170} className="p-leg__signal">
        {signal && <><span className="p-eyebrow">THEIR VOTE</span>{r.vote && <span className={`p-vote p-vote--${r.vote}`}>{r.vote.toUpperCase()}</span>}</>}
      </RowCell>
    </ListRow>
  );
}
